import { getUploadFn } from './upload';
import { canUseProxy } from './utils';
import time from './time';
import { getBatchConfig } from './batchConfig';
import BatchStorage from './batchStorage';
import BatchMonitor from './batchMonitor';

const UPLOAD_URL = 'https://gcollectsecond.octlib.com/webstore/collectData';
const UPLOAD_TIMEOUT_MS = 10000;

/**
 * 批量上报监听器
 * @description 实现批量打点上报，提高数据完整性和性能
 */
class BatchListener {
  constructor(dataLayerName, uploadType, customConfig = {}) {
    this.dataLayerName = dataLayerName;
    this.uploadType = uploadType;
    this.config = getBatchConfig(customConfig);

    // 批量队列
    this.batchQueue = [];
    // 失败队列
    this.failedQueue = [];
    // 定时器
    this.timer = null;
    // 重试定时器
    this.retryTimer = null;
    // 是否已销毁
    this.destroyed = false;
    // 上传函数
    this.uploadFn = null;
    // 初始化Promise
    this.initPromise = null;
    // 是否正在上报
    this.isUploading = false;

    // 持久化存储
    this.storage = new BatchStorage(this.config);
    // 监控模块已禁用：生产环境暂不需要，避免额外开销；需要调试时可取消注释并启用 config.monitor.enabled
    // this.monitor = new BatchMonitor(this.config);

    // 立即同步初始化 dataLayer（Proxy 需尽早拦截 push，故同步；上传等 IO 操作在 init() 中异步完成）
    if (
      Object.prototype.toString.call(window[this.dataLayerName]) !==
      '[object Array]'
    ) {
      window[this.dataLayerName] = [];
    }

    // 异步初始化
    this.initPromise = this.init();

    // 立即设置 Proxy
    this.setupProxy();

    // 暴露监控接口到全局（开发调试用）
    // if (this.config.monitor.enabled) {
    //   window[`${dataLayerName}_monitor`] = this.monitor;
    // }
  }

  /**
   * 设置 Proxy 监听
   */
  setupProxy() {
    let _this = this;
    if (canUseProxy()) {
      window[this.dataLayerName] = new Proxy(window[this.dataLayerName], {
        set(target, property, value) {
          let isDeletePoint = property == 'length' && target.length > value;
          if (Object.prototype.toString.call(value) == '[object Object]') {
            let _time = time();
            value['pointDate'] = _time.pointDate;
            value['pointTime'] = _time.pointTime;
            value['pointTimestamp'] = _time.pointTimestamp;
          }
          target[property] = value;
          if (!isDeletePoint && property == 'length') {
            // 收集打点数据
            _this.collectPoint();
          }
          return true;
        }
      });
    }
  }

  /**
   * 初始化
   */
  async init() {
    try {
      // 获取上传函数
      this.uploadFn = await getUploadFn(this.uploadType);

      // 恢复失败的打点
      await this.recoverFailedPoints();

      // 处理现有队列
      if (window[this.dataLayerName].length > 0) {
        this.collectPoint();
      }

      // 页面卸载前强制上报（保存引用便于 destroy 时移除）
      this._beforeUnloadHandler = () => this.flushAll(true);
      this._visibilityChangeHandler = () => {
        if (document && document.visibilityState === 'hidden') {
          this.flushAll(true);
        }
      };
      window.addEventListener('beforeunload', this._beforeUnloadHandler);
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', this._visibilityChangeHandler);
      }
    } catch (error) {
      console.error('Batch listener init error:', error);
    }
  }

  /**
   * 销毁实例，释放所有资源
   * @description 移除事件监听、清除定时器、关闭 IndexedDB 连接
   */
  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;

    // 取消空闲上报
    this.cancelIdleUpload();
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    // 移除事件监听器
    if (this._beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this._beforeUnloadHandler);
      this._beforeUnloadHandler = null;
    }
    if (this._visibilityChangeHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this._visibilityChangeHandler);
      this._visibilityChangeHandler = null;
    }

    // 关闭 IndexedDB 连接
    if (this.storage && typeof this.storage.close === 'function') {
      this.storage.close();
    }

    // 清空队列引用
    this.batchQueue = [];
    this.failedQueue = [];
    this.uploadFn = null;

    console.log('[BatchListener] 实例已销毁');
  }

  /**
   * 恢复失败的打点
   */
  async recoverFailedPoints() {
    try {
      const failedPoints = await this.storage.getFailedPoints();
      if (failedPoints && failedPoints.length > 0) {
        console.log(
          `[BatchListener] 恢复 ${failedPoints.length} 条失败的打点`
        );
        this.failedQueue.push(...failedPoints);
        // 启动重试
        this.scheduleRetry();
      }
    } catch (error) {
      console.error('Recover failed points error:', error);
    }
  }

  /**
   * 收集打点数据
   */
  collectPoint() {
    if (this.destroyed) return;
    // 等待初始化完成
    Promise.resolve(this.initPromise).then(() => {
      if (this.destroyed) return;
      this.processDataLayer();
    });
  }

  /**
   * 处理 dataLayer 中的数据
   */
  processDataLayer() {
    while (window[this.dataLayerName].length > 0) {
      const pointInfo = window[this.dataLayerName].shift();
      if (pointInfo) {
        // 采样控制
        if (Math.random() > this.config.sampleRate) {
          continue;
        }

        // 判断是否为重要事件（立即上报）
        // if (this.isImportantEvent(pointInfo)) {
        //   this.uploadImmediately(pointInfo);
        // } else {
          // 加入批量队列
          this.addToBatchQueue(pointInfo);
        // }
      }
    }
  }

  /**
   * 判断是否为重要事件
   * @param {Object} pointInfo - 打点数据
   * @returns {Boolean}
   */
  isImportantEvent(pointInfo) {
    const eventName = pointInfo.eventName;
    return this.config.importantEvents.includes(eventName);
  }

  /**
   * 立即上报（重要事件）
   * @param {Object} pointInfo - 打点数据
   */
  async uploadImmediately(pointInfo) {
    if (!this.uploadFn) return;

    try {
      const startTime = Date.now();
      // 使用支持错误捕获的方式上报重要事件
      await this.uploadWithErrorHandling(pointInfo);
      const delay = Date.now() - startTime;
      // this.monitor.recordSuccess(1, delay);
      console.log('[BatchListener] 重要事件立即上报成功:', pointInfo.eventName);
    } catch (error) {
      console.error('[BatchListener] 重要事件上报失败:', error.message);
      // this.monitor.recordFailure(1, error);
      // 立即上报失败，加入失败队列
      this.failedQueue.push(pointInfo);
      // 触发重试
      this.scheduleRetry();
    }
  }

  /**
   * 添加到批量队列
   * @description 上报时机：1.  JS 空闲时 2. 页面卸载/可见性变化时
   * @param {Object} pointInfo - 打点数据
   */
  addToBatchQueue(pointInfo) {
    if (this.destroyed) return;

    this.batchQueue.push(pointInfo);
    // this.monitor.recordQueueSize(this.batchQueue.length);

    // JS 空闲时：安排空闲时上报
    this.scheduleIdleUpload();
  }

  /**
   * 安排空闲时上报
   * @description 使用 requestIdleCallback 在 JS 空闲时上报，不支持时降级为 setTimeout
   */
  scheduleIdleUpload() {
    this.cancelIdleUpload();

    const doUpload = () => {
      this.timer = null;
      if (this.batchQueue.length > 0) {
        this.triggerBatchUpload();
      }
    };

    if (typeof requestIdleCallback !== 'undefined') {
      // this.timer = requestIdleCallback(doUpload, {
      //   timeout: this.config.maxWaitTime
      // });
      this.timer = requestIdleCallback(doUpload);
    } else {
      // this.timer = setTimeout(doUpload, this.config.maxWaitTime);
      this.timer = setTimeout(doUpload, 0);
    }
  }

  /**
   * 取消空闲上报
   */
  cancelIdleUpload() {
    if (!this.timer) return;
    if (typeof cancelIdleCallback !== 'undefined') {
      cancelIdleCallback(this.timer);
    } else {
      clearTimeout(this.timer);
    }
    this.timer = null;
  }

  /**
   * 触发批量上报
   */
  async triggerBatchUpload() {
    if (this.destroyed) return;
    // 取消空闲上报
    this.cancelIdleUpload();

    // 防止并发上报
    if (this.isUploading || this.batchQueue.length === 0) {
      return;
    }

    this.isUploading = true;

    // 取出当前批次数据
    const batch = this.batchQueue.splice(0, this.config.batchSize);

    try {
      const startTime = Date.now();
      await this.uploadBatch(batch);
      const delay = Date.now() - startTime;
      // this.monitor.recordSuccess(batch.length, delay);
    } catch (error) {
      console.error('Batch upload error:', error);
      // this.monitor.recordFailure(batch.length, error);
      // 上报失败，加入失败队列
      this.handleUploadFailure(batch);
    } finally {
      this.isUploading = false;

      // 如果队列中还有数据，安排空闲时上报
      if (this.batchQueue.length > 0) {
        this.scheduleIdleUpload();
      }
    }
  }

  /**
   * 将批次数据序列化为批量上报格式
   * @param {Array} batch - 批次数据
   * @returns {Object} 批量上报数据
   */
  serializeBatchData(batch) {
    const batchPoints = batch.map(point => ({
      eventName: point.eventName || '',
      eventType: point.eventType || '',
      eventValue: point.eventValue || ''
    }));

    const firstPoint = batch[0] || {};
    const { eventName, eventType, eventValue, ...commonFields } = firstPoint;

    return {
      ...commonFields,
      eventType: 'testBatchPoint',
      eventName: 'testBatchPoint',
      eventValue: batchPoints,
      _batchMeta: {
        batchSize: batch.length,
        batchTime: Date.now(),
        originalPointsCount: batch.length
      }
    };
  }

  /**
   * 批量上报
   * @param {Array} batch - 批次数据
   * @param {Boolean} useBeacon - 是否使用sendBeacon（页面卸载时）
   */
  async uploadBatch(batch, useBeacon = false) {
    if (!this.uploadFn || !batch || batch.length === 0) {
      return;
    }

    const batchData = this.serializeBatchData(batch);

    try {
      await this.uploadFn(batchData, true);

      console.log(
        `[BatchListener] 批量上报成功: ${batch.length} 条点位合并上报`,
        batchData.eventValue
      );
    } catch (error) {
      console.error('Batch upload error:', error);
      throw error;
    }
  }

  /**
   * 将上报数据序列化为 URLSearchParams
   * @param {Object} data - 上报数据
   * @returns {URLSearchParams}
   */
  serializeUploadParams(data) {
    const params = new URLSearchParams();
    for (let key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const value = data[key];
        if (key === 'eventValue' || key === '_batchMeta') {
          params.append(key, typeof value === 'object' && value !== null
            ? JSON.stringify(value)
            : String(value));
        } else if (value !== null && value !== undefined) {
          params.append(key, String(value));
        }
      }
    }
    return params;
  }

  /**
   * 发送上报请求
   * @param {string} url - 上报地址
   * @param {URLSearchParams} params - 请求参数
   * @param {number} timeoutMs - 超时时间（毫秒）
   * @returns {Promise<Response>}
   */
  async sendUploadRequest(url, params, timeoutMs = UPLOAD_TIMEOUT_MS) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        method: 'POST',
        body: params,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * 处理上报错误，统一错误格式
   * @param {Error} error - 原始错误
   * @returns {Error}
   */
  handleUploadError(error) {
    const errorMessage = error.name === 'AbortError'
      ? 'Request timeout'
      : error.message;
    console.error('[BatchListener] Upload failed:', errorMessage);
    return new Error(errorMessage);
  }

  /**
   * 支持错误捕获的上报方式
   * @param {Object} data - 上报数据
   */
  async uploadWithErrorHandling(data) {
    try {
      const params = this.serializeUploadParams(data);
      const response = await this.sendUploadRequest(
        UPLOAD_URL,
        params,
        UPLOAD_TIMEOUT_MS
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      throw this.handleUploadError(error);
    }
  }

  /**
   * 处理上报失败
   * @param {Array} batch - 失败的批次数据
   */
  handleUploadFailure(batch) {
    // 标记重试次数
    batch.forEach(point => {
      point._retryCount = (point._retryCount || 0) + 1;
    });

    // 检查是否超过最大重试次数
    const canRetry = batch.filter(
      point => point._retryCount <= this.config.retryTimes
    );
    const tooManyRetries = batch.filter(
      point => point._retryCount > this.config.retryTimes
    );

    // 可重试的加入失败队列
    if (canRetry.length > 0) {
      this.failedQueue.push(...canRetry);
      this.scheduleRetry();
    }

    // 超过重试次数的持久化存储
    if (tooManyRetries.length > 0) {
      console.warn(
        `[BatchListener] ${tooManyRetries.length} 条数据超过最大重试次数，持久化存储`
      );
      this.storage.saveFailedPoints(tooManyRetries);
    }
  }

  /**
   * 安排重试
   */
  scheduleRetry() {
    if (this.failedQueue.length === 0 || this.destroyed) return;

    // 取出第一条失败数据的重试次数，计算延迟
    const retryCount = this.failedQueue[0]?._retryCount || 1;
    const delay = this.config.retryDelay * Math.pow(2, retryCount - 1);

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.retryFailedPoints();
    }, delay);
  }

  /**
   * 重试失败的打点
   */
  async retryFailedPoints() {
    if (this.failedQueue.length === 0 || this.isUploading || this.destroyed) {
      return;
    }

    this.isUploading = true;

    // 取出一批数据重试
    const batch = this.failedQueue.splice(0, this.config.batchSize);

    try {
      const startTime = Date.now();
      await this.uploadBatch(batch);
      const delay = Date.now() - startTime;
      // this.monitor.recordSuccess(batch.length, delay);

      // 重试成功，从持久化存储中删除
      await this.storage.deletePoints(batch);
    } catch (error) {
      console.error('Retry failed points error:', error);
      // this.monitor.recordFailure(batch.length, error);
      // 重试失败，重新加入失败队列
      this.handleUploadFailure(batch);
    } finally {
      this.isUploading = false;

      // 继续重试剩余数据
      if (this.failedQueue.length > 0) {
        this.scheduleRetry();
      }
    }
  }

  /**
   * 强制上报所有数据（页面卸载时）
   * @param {Boolean} useBeacon - 是否使用sendBeacon
   */
  flushAll(useBeacon = false) {
    if (this.destroyed) return;
    // 处理dataLayer中的数据
    this.processDataLayer();

    // 上报批量队列（合并成批量数据）
    if (this.batchQueue.length > 0) {
      const batch = [...this.batchQueue];
      this.batchQueue = [];

      if (batch.length > 0) {
        try {
          if (batch.length === 1) {
            // 单条直接上报
            this.uploadFn(batch[0], useBeacon);
            console.log('[BatchListener] 卸载时单条上报');
          } else {
            // 多条使用 uploadBatch 合并上报
            console.log(`[BatchListener] 卸载时批量上报: ${batch.length} 条点位`);
            this.uploadBatch(batch, useBeacon).catch(error => {
              console.error('[BatchListener] Flush batch error:', error);
            });
          }
        } catch (error) {
          console.error('[BatchListener] Flush batch error:', error);
        }
      }
    }

    // 上报失败队列（合并成批量数据）
    if (this.failedQueue.length > 0) {
      const failed = [...this.failedQueue];
      this.failedQueue = [];

      if (failed.length > 0) {
        try {
          if (failed.length === 1) {
            // 单条直接上报
            this.uploadFn(failed[0], useBeacon);
            console.log('[BatchListener] 卸载时失败队列单条上报');
          } else {
            // 多条使用 uploadBatch 合并上报
            console.log(`[BatchListener] 卸载时批量上报失败队列: ${failed.length} 条点位`);
            this.uploadBatch(failed, useBeacon).catch(error => {
              console.error('[BatchListener] Flush failed queue error:', error);
            });
          }
        } catch (error) {
          console.error('[BatchListener] Flush failed queue error:', error);
        }
      }
    }
  }

  /**
   * 获取监控报告
   */
  getMonitorReport() {
    return this.monitor ? this.monitor.getReport() : null;
  }

  /**
   * 打印监控报告
   */
  printMonitorReport() {
    if (this.monitor) {
      this.monitor.printReport();
    }
  }
}

export default BatchListener;
